// Pure helpers for the trainer's plan builder (pages/PlanBuilderPage.js).
//
// A block (warm-up, rolling, stretching...) is stored in `circuit_group`, a
// superset in `superset_group`: a letter shared by neighbouring exercises.

const ROLLING_NOTE = 'Powoli, nacisk do odczuwalnego, nie ostrego bólu. Dobre po długim siedzeniu.';
const STRETCH_NOTE = 'Delikatnie, do lekkiego rozciągnięcia, spokojny oddech.';

export const BLOCK_ROLLING = 'Rolowanie';
export const BLOCK_STRETCHING = 'Rozciąganie';
export const BLOCK_MAIN = 'main'; // Select value of the main part; stored as circuit_group = null
export const PLAN_BLOCKS = [
  [BLOCK_MAIN, 'Główna część'], [BLOCK_ROLLING, BLOCK_ROLLING], ['Rozgrzewka', 'Rozgrzewka'],
  ['Zakończenie', 'Zakończenie'], [BLOCK_STRETCHING, BLOCK_STRETCHING],
];

const ROLLING_SET = [
  ['Mobilizacja Piersiowa na Wałku', '60 s'],
  ['Rolowanie przodu uda wałkiem', '60 s / stronę'],
  ['Rolowanie TFL / pasma biodrowo-piszczelowego', '45 s / stronę'],
  ['Rolowanie pośladków piłeczką', '60 s / stronę'],
];
const STRETCH_LOWER = ['Rozciąganie mięśnia czworogłowego', 'Rozciąganie pośladka „figure 4” leżąc'];
const STRETCH_UPPER = ['Rozciąganie mięśnia piersiowego w narożniku', 'Rozciąganie górnej części mięśnia czworobocznego'];
const LOWER_PATTERNS = ['squat', 'hinge', 'lunge', 'calf', 'isolation_leg'];
const UPPER_PATTERNS = ['push_h', 'push_v', 'pull_h', 'pull_v', 'isolation_arm', 'isolation_shoulder', 'isolation_chest'];

export const dayItems = (all, day) => all
  .filter(e => e.day_number === day)
  .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));

// The whole plan with one day's exercises replaced and renumbered.
export const withDay = (all, day, items) => [
  ...all.filter(e => e.day_number !== day),
  ...items.map((e, i) => ({ ...e, day_number: day, order_index: i })),
].sort((a, b) => a.day_number - b.day_number);

export const newPlanItem = (exercise, day, fields = {}) => ({
  exercise_id: exercise.id,
  exercise_name: exercise.name,
  day_number: day,
  order_index: 0,
  sets: 3,
  reps: '10',
  tempo: '2-0-2-0',
  rir: 2,
  rpe: 7,
  rest_seconds: 90,
  weight_kg: null,
  notes: '',
  superset_group: null,
  circuit_group: null,
  ...fields,
});

const addOn = (exercise, day, reps, circuit_group, notes) => newPlanItem(exercise, day, {
  sets: 1, reps, tempo: null, rir: null, rpe: null, rest_seconds: 0, circuit_group, notes,
});

const byName = (library, name) => library.find(e => e.name.toLowerCase() === name.toLowerCase());

export const rollingItems = (library, day) => ROLLING_SET
  .map(([name, reps]) => [byName(library, name), reps])
  .filter(([ex]) => ex)
  .map(([ex, reps]) => addOn(ex, day, reps, BLOCK_ROLLING, ROLLING_NOTE));

// Two stretches per body part trained that day, whole body when it can't be told.
export const stretchingItems = (library, items, day) => {
  const patterns = items.map(i => library.find(e => e.id === i.exercise_id)?.pattern);
  const lower = patterns.some(p => LOWER_PATTERNS.includes(p));
  const upper = patterns.some(p => UPPER_PATTERNS.includes(p));
  const names = [...(lower || !upper ? STRETCH_LOWER : []), ...(upper || !lower ? STRETCH_UPPER : [])];
  return names
    .map(name => byName(library, name))
    .filter(Boolean)
    .map(ex => addOn(ex, day, '30 s / stronę', BLOCK_STRETCHING, STRETCH_NOTE));
};

export const hasBlock = (items, block) => items.some(e => e.circuit_group === block);

// A swap keeps the dose, the block and the superset; the load belongs to the old exercise.
export const swapExercise = (item, exercise) => ({
  ...item, exercise_id: exercise.id, exercise_name: exercise.name, weight_kg: null,
});

export const similarExercises = (current, library) => {
  const base = library.find(e => e.id === current?.exercise_id);
  return base?.pattern ? library.filter(e => e.pattern === base.pattern && e.id !== base.id) : [];
};

// First and last index of the superset holding `i` (just `i` outside a superset).
export const runOf = (items, i) => {
  const group = items[i].superset_group;
  let [start, end] = [i, i];
  if (!group) return [start, end];
  while (start > 0 && items[start - 1].superset_group === group) start -= 1;
  while (end + 1 < items.length && items[end + 1].superset_group === group) end += 1;
  return [start, end];
};

// Letters follow the order of the day; a lone exercise is no longer a superset.
const relabel = (items) => {
  const out = [];
  let letter = 0;
  for (let i = 0; i < items.length;) {
    const [, end] = runOf(items, i);
    const group = end > i ? String.fromCharCode(65 + letter++) : null;
    for (; i <= end; i += 1) out.push({ ...items[i], superset_group: group });
  }
  return out;
};

export const isLinked = (items, i) => !!items[i].superset_group
  && items[i].superset_group === items[i + 1]?.superset_group;

// Connect `i` with the next exercise, or cut the superset between them.
export const toggleSuperset = (items, i) => {
  const [start] = runOf(items, i);
  const [, end] = runOf(items, i + 1);
  const [from, tag] = isLinked(items, i) ? [i + 1, 'split'] : [start, 'join'];
  return relabel(items.map((e, k) => (k >= from && k <= end ? { ...e, superset_group: tag } : e)));
};

export const moveItem = (items, i, dir) => {
  const [start, end] = runOf(items, i);
  const target = i + dir;
  if (target >= start && target <= end) { // reorder inside a superset
    const out = [...items];
    [out[i], out[target]] = [out[target], out[i]];
    return out;
  }
  const edge = dir < 0 ? start - 1 : end + 1;
  if (edge < 0 || edge >= items.length) return items;
  const [nStart, nEnd] = runOf(items, edge); // a superset travels whole, past a whole neighbour
  const unit = items.slice(start, end + 1);
  const neighbour = items.slice(nStart, nEnd + 1);
  const out = [...items];
  out.splice(Math.min(start, nStart), unit.length + neighbour.length,
    ...(dir < 0 ? [...unit, ...neighbour] : [...neighbour, ...unit]));
  return relabel(out);
};

export const removeItem = (items, i) => relabel(items.filter((_, k) => k !== i));

export const setBlock = (items, i, value) => relabel(items.map((e, k) => (k === i
  ? { ...e, circuit_group: value === BLOCK_MAIN ? null : value, superset_group: null } : e)));
